import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { ErpCapabilities, ErpConnectionInfo } from '../types';

/**
 * Šta aplikacija sme u tuđem ERP-u — izmereno, ne pročitano iz koda.
 *
 * ZAŠTO OVAJ EKRAN POSTOJI
 *   Prekidači stoje u `web.config`-u NA SERVERU, koji se pri postavljanju namerno
 *   ne prepisuje (nosi lozinke). Posledica: iz koda se ne vidi šta je uključeno, a
 *   razlika je ogromna — isti ekran sa uključenim upisom menja tuđe knjige.
 *
 *   Na tome se već izgubilo vreme: kroz celu proveru u septembru 2026. tvrdilo se
 *   da je upis isključen, a na serveru je radio od 04.09. i za tri nedelje označio
 *   330 otpremnica.
 *
 *   Isto važi za prava u bazi. Ona se ume dati po KOLONAMA, a provera na nivou
 *   tabele tada vraća nulu — pa je i to jednom pročitano naopako.
 *
 * Lozinka se ne prikazuje i ne stiže sa servera, ni maskirana. Vidi se samo ime
 * baze i ime naloga.
 */
export default function ErpAccess() {
  const [stanje, setStanje] = useState<ErpCapabilities | null>(null);
  const [nijeStiglo, setNijeStiglo] = useState(false);

  const load = useCallback(async () => {
    try {
      setStanje(await api.erpCapabilities());
      setNijeStiglo(false);
    } catch {
      setStanje(null);
      setNijeStiglo(true);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (nijeStiglo) {
    return (
      <section className="panel">
        <h2>Pristup ERP-u</h2>
        <div className="health health--bad">
          Stanje nije stiglo sa servera. Prazno <strong>ne znači</strong> da
          aplikacija ništa ne sme — znači da se nije moglo pročitati.
        </div>
      </section>
    );
  }

  if (!stanje) {
    return (
      <section className="panel">
        <h2>Pristup ERP-u</h2>
        <p className="muted">Učitavam…</p>
      </section>
    );
  }

  return (
    <section className="panel">
      <h2>Pristup ERP-u</h2>

      <p className="note">
        Ovo se ne čita iz koda nego se pita server i baza. Prekidači stoje u
        <code> web.config</code>-u na serveru, koji se pri postavljanju ne
        prepisuje — pa je ovo jedino mesto gde se vidi šta je stvarno uključeno.
        Posle izmene u <code>web.config</code>-u sajt mora da se restartuje.
      </p>

      <ul className="rows">
        <Veza info={stanje.read} />
        <Veza info={stanje.write} />
      </ul>

      <h3>Šta aplikacija sme</h3>

      <ul className="rows">
        <Prekidac
          on={stanje.allowWrites}
          ime="Upis u ERP"
          setting="Erp__AllowWrites"
          daKaze="Aplikacija pravi naloge i označava isporučene otpremnice."
          neKaze="Aplikacija ništa ne upisuje; nalog pravi čovek u ERP-u." />

        <Prekidac
          on={stanje.autoReserve}
          ime="Prva noga sama (izvor → 012)"
          setting="Erp__AutoReserve"
          daKaze="Roba se odvaja čim je poznato odakle ide."
          neKaze="Prvu nogu pravi čovek posle poruke; aplikacija samo gleda da li se desilo." />

        <Prekidac
          on={stanje.allowDelete}
          ime="Brisanje svojih naloga"
          setting="Erp__AllowDelete"
          daKaze="Kad poslovođa promeni mesto, nalog sa pogrešnog se briše i pravi se nov."
          neKaze="Izmena mesta ide kroz ERP — poslovođa dobija broj naloga i zove magacin." />
      </ul>

      <h3>Prava naloga za upis</h3>

      {stanje.permissions.length === 0 ? (
        <p className="muted">
          Prava se nisu mogla pročitati. To <strong>nije</strong> isto što i „nema
          prava" — veza za upis nije odgovorila.
        </p>
      ) : (
        <>
          <div className="table-scroll">
            <table className="prava">
              <thead>
                <tr>
                  <th>tabela</th><th>čita</th><th>upisuje</th><th>menja</th><th>briše</th>
                </tr>
              </thead>
              <tbody>
                {stanje.permissions.map((p) => (
                  <tr key={p.table}>
                    <td><code>{p.table}</code></td>
                    <td>{p.select ? 'da' : '—'}</td>
                    <td>{p.insert ? 'da' : '—'}</td>
                    <td>{p.update ? 'da' : '—'}</td>
                    <td>{p.delete ? 'da' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="note">
            Kod <code>NALKASE</code> se „menja" pita za kolonu <code>ISPORUCENO</code>,
            ne za celu tabelu: pravo je tamo namerno suženo na dve kolone, pa provera
            na nivou tabele vraća „ne" iako označavanje isporuke radi.
          </p>
        </>
      )}
    </section>
  );
}

function Veza({ info }: { info: ErpConnectionInfo }) {
  return (
    <li>
      <span className="row__name">Veza za {info.purpose}</span>

      <span className="row__meta">
        {info.reachable
          ? <>baza <code>{info.database}</code> · nalog <code>{info.user}</code></>
          : info.configured
            ? `podešena, ali ne odgovara${info.failure ? `: ${info.failure}` : ''}`
            : 'nije podešena'}
      </span>

      <span className="row__side">
        <span className={info.reachable ? 'flag flag--ok' : 'flag flag--bad'}>
          {info.reachable ? 'radi' : 'ne radi'}
        </span>
      </span>
    </li>
  );
}

/** Jedan prekidač, sa rečenicom šta znači UKLJUČEN, a šta ISKLJUČEN. */
function Prekidac(
  { on, ime, setting, daKaze, neKaze }:
  { on: boolean; ime: string; setting: string; daKaze: string; neKaze: string }
) {
  return (
    <li>
      <span className="row__name">{ime}</span>
      <span className="row__meta">
        {on ? daKaze : neKaze} <code>{setting}</code>
      </span>
      <span className="row__side">
        <span className={on ? 'flag flag--ok' : 'flag'}>
          {on ? 'uključeno' : 'isključeno'}
        </span>
      </span>
    </li>
  );
}
